import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  usePreferences,
  useSetPreference,
  PREFERENCE_DEFAULTS,
} from '@/hooks/usePreferences';
import type { UserPreferences } from '@/services/api/endpoints';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
  },
  option: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    // Aligné sur le libellé de l'interrupteur, pas sur le bord du dialogue : la
    // glissière de Fluent occupe une largeur fixe à gauche du texte.
    paddingLeft: '44px',
  },
  loading: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
});

interface Option {
  key: keyof UserPreferences;
  label: string;
  hint: string;
}

/**
 * L'ORDRE COMPTE : du plus anodin au plus lourd de conséquences. Les deux
 * catégorisations ne font qu'ajouter une étiquette, et se suivent parce qu'elles
 * répondent à la même question ; « Envoyer et ranger » change un geste d'envoi ;
 * la suppression touche au message lui-même et vient donc en dernier, là où on ne
 * l'active pas par inadvertance en cherchant autre chose.
 */
const OPTIONS: Option[] = [
  {
    key: 'autoCategorize',
    label: 'Catégoriser les mails rangés',
    hint: 'Applique la catégorie « Rangé » au mail dans Outlook.',
  },
  {
    key: 'categorizeProjectNumber',
    // L'infobulle ne mentionne PLUS le cas « pas de numéro » (espace d'équipe, ou
    // projet portant un intitulé interne du genre BIM ou MODELE) : ne pas
    // étiqueter est alors le comportement, et l'écrire ici obligeait à lire deux
    // phrases pour comprendre l'interrupteur. Le silence reste voulu, il est
    // décidé et testé dans `services/office/categories.ts`.
    label: 'Catégoriser par numéro d’affaire',
    hint: 'Applique la catégorie « 00000 » du projet où le mail est rangé.',
  },
  {
    key: 'sendAndFile',
    label: 'Proposer « Envoyer et ranger »',
    hint: 'Affiche une bannière sur vos brouillons pour ranger le mail au moment de l’envoyer.',
  },
  {
    key: 'deleteAfterFiling',
    label: 'Supprimer le mail après rangement',
    hint: 'Déplace le mail d’origine vers les Éléments supprimés une fois rangé.',
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Préférences de l'utilisateur courant — accessibles à TOUS, contrairement au
 * panneau Administration.
 *
 * Chaque interrupteur enregistre IMMÉDIATEMENT (comme les rôles du panneau
 * admin) : il n'y a donc ni bouton « Enregistrer » ni état local à réconcilier,
 * et fermer la fenêtre ne perd rien. En contrepartie il faut désactiver la
 * fenêtre pendant l'écriture, sinon un second clic partirait avant que le
 * premier ne soit revenu.
 */
export function PreferencesDialog({ open, onOpenChange }: Props) {
  const styles = useStyles();
  const { data, isLoading, error: loadError } = usePreferences();
  const setPref = useSetPreference();

  // Jamais d'interrupteur dans un état indéterminé : à défaut de valeur lue, on
  // montre le défaut (désactivé), qui est aussi ce que le code d'action
  // appliquera. L'affichage et le comportement ne peuvent donc pas diverger.
  const values = data ?? PREFERENCE_DEFAULTS;
  const readError = loadError instanceof Error ? loadError.message : null;
  const writeError = setPref.error instanceof Error ? setPref.error.message : null;

  return (
    <Dialog open={open} onOpenChange={(_e, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Préférences</DialogTitle>
          <DialogContent className={styles.content}>
            {isLoading ? (
              <div className={styles.loading}>
                <Spinner size="tiny" />
                <Text>Chargement…</Text>
              </div>
            ) : (
              <>
                {readError && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      Préférences illisibles ({readError}). Les options
                      s'affichent désactivées et le resteront jusqu'au
                      rétablissement.
                    </MessageBarBody>
                  </MessageBar>
                )}
                {OPTIONS.map((opt) => (
                  <div key={opt.key} className={styles.option}>
                    <Switch
                      checked={values[opt.key]}
                      label={opt.label}
                      // Désactivé pendant l'écriture, y compris les autres
                      // interrupteurs : deux réglages simultanés sont deux
                      // requêtes concurrentes sur la même ligne.
                      disabled={setPref.isPending || Boolean(readError)}
                      onChange={(_e, d) =>
                        setPref.mutate({ [opt.key]: d.checked } as Partial<UserPreferences>)
                      }
                    />
                    <Text className={styles.hint}>{opt.hint}</Text>
                  </div>
                ))}
                {writeError && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      Réglage non enregistré : {writeError}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
